import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Popover,
  Tooltip
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { createComment, getCommentsByEpisodeId, toggleCommentReaction } from '../../services/commentService';
import PersonIcon from '@mui/icons-material/Person';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import EmojiPicker from 'emoji-picker-react';

const EpisodeComments = ({ episodeId, userCharacters = [] }) => {
  const { currentUser, firestoreUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Comment filter state
  const [commentFilter, setCommentFilter] = useState('all'); // 'all', 'ic', 'ooc'
    // Comment form state
  const [commentContent, setCommentContent] = useState('');
  const [isInCharacter, setIsInCharacter] = useState(false);
  const [selectedCharacterId, setSelectedCharacterId] = useState('');
    // Emoji picker state
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState(null);
  const textFieldRef = useRef(null);
  
  // Reaction emoji picker state
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState(null);
  const [reactionPickerCommentId, setReactionPickerCommentId] = useState(null);

  useEffect(() => {
    const fetchComments = async () => {
      if (!episodeId) return;
      
      setLoading(true);
      setError('');
      try {
        const episodeComments = await getCommentsByEpisodeId(episodeId);
        setComments(episodeComments);
      } catch (err) {
        console.error("Error fetching comments:", err);
        setError('Failed to load comments.');
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [episodeId]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentContent.trim()) {
      setError('Comment content is required.');
      return;
    }
    
    if (isInCharacter && !selectedCharacterId) {
      setError('Please select a character for in-character comments.');
      return;
    }
    
    if (!currentUser || !firestoreUser) {
      setError('You must be logged in to post comments.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const selectedCharacter = userCharacters.find(char => char.id === selectedCharacterId);      const commentData = {
        episodeId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
        userPhotoURL: currentUser.photoURL || null, // Include user's Google profile photo
        content: commentContent.trim(),
        isInCharacter,
        ...(isInCharacter && selectedCharacter && {
          characterId: selectedCharacter.id,
          characterName: selectedCharacter.name,
          characterImageUrl: selectedCharacter.imageUrl || null // Include character image for IC comments
        })
      };

      const commentId = await createComment(commentData);
      
      // Add the new comment to local state
      const newComment = {
        id: commentId,
        ...commentData,
        createdAt: { seconds: Date.now() / 1000 } // Approximate timestamp for immediate display
      };
      
      setComments(prev => [newComment, ...prev]);
      setCommentContent('');
      setIsInCharacter(false);
      setSelectedCharacterId('');
    } catch (err) {
      console.error("Error posting comment:", err);
      setError(err.message || 'Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString();
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const textField = textFieldRef.current?.querySelector('textarea');
    
    if (textField) {
      const start = textField.selectionStart;
      const end = textField.selectionEnd;
      const newText = commentContent.slice(0, start) + emoji + commentContent.slice(end);
      setCommentContent(newText);
      
      // Close emoji picker
      setEmojiPickerAnchor(null);
      
      // Focus back to text field and set cursor position
      setTimeout(() => {
        textField.focus();
        textField.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 100);
    } else {
      // Fallback: just append to end
      setCommentContent(prev => prev + emoji);
      setEmojiPickerAnchor(null);
    }
  };

  const handleEmojiButtonClick = (event) => {
    setEmojiPickerAnchor(event.currentTarget);
  };
  const handleEmojiPickerClose = () => {
    setEmojiPickerAnchor(null);
  };
  const handleReactionClick = async (commentId, emoji) => {
    if (!currentUser || !firestoreUser) return;
    
    try {
      const userName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous';
      await toggleCommentReaction(commentId, emoji, currentUser.uid, userName);
      
      // Refresh comments to show updated reactions
      const updatedComments = await getCommentsByEpisodeId(episodeId);
      setComments(updatedComments);
    } catch (err) {
      console.error("Error updating reaction:", err);
      setError('Failed to update reaction. Please try again.');
    }
  };

  const handleReactionEmojiPickerOpen = (event, commentId) => {
    setReactionPickerAnchor(event.currentTarget);
    setReactionPickerCommentId(commentId);
  };

  const handleReactionEmojiPickerClose = () => {
    setReactionPickerAnchor(null);
    setReactionPickerCommentId(null);
  };

  const handleReactionEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    if (reactionPickerCommentId) {
      handleReactionClick(reactionPickerCommentId, emoji);
    }
    handleReactionEmojiPickerClose();
  };
  const getReactionCount = (reactions, emoji) => {
    return reactions?.[emoji]?.length || 0;
  };

  const hasUserReacted = (reactions, emoji, userId) => {
    return reactions?.[emoji]?.some(reaction => reaction.userId === userId) || false;
  };

  const getReactionTooltip = (reactions, emoji) => {
    const emojiReactions = reactions?.[emoji] || [];
    if (emojiReactions.length === 0) return '';
    
    const userNames = emojiReactions.map(reaction => reaction.userName || 'Unknown User');
    if (userNames.length === 1) {
      return userNames[0];
    } else if (userNames.length === 2) {
      return `${userNames[0]} and ${userNames[1]}`;
    } else if (userNames.length <= 5) {
      return `${userNames.slice(0, -1).join(', ')}, and ${userNames[userNames.length - 1]}`;
    } else {
      return `${userNames.slice(0, 3).join(', ')}, and ${userNames.length - 3} others`;
    }
  };

  const commonReactions = ['👍', '👎', '😄', '❤️', '😮', '😢'];

  const activeCharacters = userCharacters.filter(char => char.userId === currentUser?.uid);
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif', mb: 2 }}>
        Episode Comments
      </Typography>

      {/* Comment Filter Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={commentFilter}
          exclusive
          onChange={(event, newFilter) => {
            if (newFilter !== null) {
              setCommentFilter(newFilter);
            }
          }}
          aria-label="comment filter"
          sx={{
            '& .MuiToggleButton-root': {
              color: '#F5F5F5',
              borderColor: '#d98d2a',
              fontFamily: '"Georgia", serif',
              '&.Mui-selected': {
                backgroundColor: '#d98d2a',
                color: '#1c1c1c',
                '&:hover': {
                  backgroundColor: '#c87d26',
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(217, 141, 42, 0.1)',
              }
            }
          }}
        >
          <ToggleButton value="all" aria-label="show all comments">
            All Comments
          </ToggleButton>
          <ToggleButton value="ic" aria-label="show in-character comments">
            <TheaterComedyIcon sx={{ mr: 1 }} />
            In Character
          </ToggleButton>
          <ToggleButton value="ooc" aria-label="show out-of-character comments">
            <PersonIcon sx={{ mr: 1 }} />
            Out of Character
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>      {/* Comments List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress sx={{ color: '#d98d2a' }} />
        </Box>
      ) : (() => {
          const filteredComments = comments.filter(comment => {
            if (commentFilter === 'all') return true;
            if (commentFilter === 'ic') return comment.isInCharacter;
            if (commentFilter === 'ooc') return !comment.isInCharacter;
            return true;
          });

          if (comments.length === 0) {
            return (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#444', border: '1px solid #d98d2a', mb: 3 }}>
                <Typography sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif' }}>
                  No comments yet. Be the first to share your thoughts!
                </Typography>
              </Paper>
            );
          }

          if (filteredComments.length === 0) {
            const filterText = commentFilter === 'ic' ? 'in-character' : 'out-of-character';
            return (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#444', border: '1px solid #d98d2a', mb: 3 }}>
                <Typography sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif' }}>
                  No {filterText} comments yet. Try switching the filter or be the first to comment!
                </Typography>
              </Paper>
            );
          }

          return (
            <Box sx={{ mb: 3 }}>
              {filteredComments.map((comment) => (
            <Paper 
              key={comment.id} 
              sx={{ 
                p: 2, 
                mb: 2, 
                backgroundColor: comment.isInCharacter ? '#2d4a3e' : '#3c3c3c',
                border: comment.isInCharacter ? '1px solid #4caf50' : '1px solid #555',
                borderLeft: comment.isInCharacter ? '4px solid #66bb6a' : '4px solid #d98d2a'
              }}
            >              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>                {/* User Avatar or Character Icon */}
                {comment.isInCharacter ? (
                  comment.characterImageUrl ? (
                    <Avatar 
                      src={comment.characterImageUrl}
                      alt={comment.characterName}
                      sx={{ 
                        width: 40, 
                        height: 40,
                        border: '2px solid #4caf50'
                      }}
                    />
                  ) : (
                    <Avatar 
                      sx={{ 
                        bgcolor: '#4caf50', 
                        width: 40, 
                        height: 40 
                      }}
                    >
                      <TheaterComedyIcon />
                    </Avatar>
                  )
                ) : comment.userPhotoURL ? (
                  <Avatar 
                    src={comment.userPhotoURL}
                    alt={comment.userName}
                    sx={{ 
                      width: 40, 
                      height: 40,
                      border: '2px solid #d98d2a'
                    }}
                  />
                ) : (
                  <Avatar 
                    sx={{ 
                      bgcolor: '#d98d2a', 
                      width: 40, 
                      height: 40 
                    }}
                  >
                    {comment.userName?.charAt(0)?.toUpperCase() || <PersonIcon />}
                  </Avatar>
                )}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: comment.isInCharacter ? '#81c784' : '#d98d2a', 
                        fontWeight: 'bold',
                        fontFamily: '"Georgia", serif'
                      }}
                    >
                      {comment.isInCharacter && comment.characterName 
                        ? `${comment.characterName} (${comment.userName})`
                        : comment.userName
                      }
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#A9A9A9' }}>
                      {formatTimestamp(comment.createdAt)}
                    </Typography>
                    {comment.isInCharacter && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#81c784', 
                          fontStyle: 'italic',
                          fontFamily: '"Georgia", serif'
                        }}
                      >
                        In Character
                      </Typography>
                    )}
                  </Box>                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#F5F5F5', 
                      whiteSpace: 'pre-wrap',
                      fontFamily: '"Georgia", serif',
                      fontStyle: comment.isInCharacter ? 'italic' : 'normal',
                      fontSize: '1.1rem', // Bigger font size
                      lineHeight: 1.6,
                      mb: 2
                    }}
                  >
                    {comment.content}                  </Typography>
                    {/* Emoji Reactions */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>                    {commonReactions.map((emoji) => {
                      const count = getReactionCount(comment.reactions, emoji);
                      const userReacted = hasUserReacted(comment.reactions, emoji, currentUser?.uid);
                      const tooltipText = getReactionTooltip(comment.reactions, emoji);
                      
                      if (count === 0 && !userReacted) return null;
                      
                      return (
                        <Tooltip key={emoji} title={tooltipText} arrow>
                          <Button
                            size="small"
                            variant={userReacted ? "contained" : "outlined"}
                            onClick={() => handleReactionClick(comment.id, emoji)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.875rem',
                              backgroundColor: userReacted ? 'rgba(217, 141, 42, 0.2)' : 'transparent',
                              borderColor: '#d98d2a',
                              color: userReacted ? '#d98d2a' : '#A9A9A9',
                              '&:hover': {
                                backgroundColor: 'rgba(217, 141, 42, 0.1)',
                                borderColor: '#d98d2a',
                              }
                            }}
                          >
                            {emoji} {count > 0 && count}
                          </Button>
                        </Tooltip>
                      );
                    })}
                      {/* Show other emoji reactions that aren't in commonReactions */}
                    {Object.entries(comment.reactions || {}).map(([emoji, reactions]) => {
                      if (commonReactions.includes(emoji)) return null;
                      const count = reactions.length;
                      const userReacted = hasUserReacted(comment.reactions, emoji, currentUser?.uid);
                      const tooltipText = getReactionTooltip(comment.reactions, emoji);
                      
                      return (
                        <Tooltip key={emoji} title={tooltipText} arrow>
                          <Button
                            size="small"
                            variant={userReacted ? "contained" : "outlined"}
                            onClick={() => handleReactionClick(comment.id, emoji)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.875rem',
                              backgroundColor: userReacted ? 'rgba(217, 141, 42, 0.2)' : 'transparent',
                              borderColor: '#d98d2a',
                              color: userReacted ? '#d98d2a' : '#A9A9A9',
                              '&:hover': {
                                backgroundColor: 'rgba(217, 141, 42, 0.1)',
                                borderColor: '#d98d2a',
                              }
                            }}
                          >
                            {emoji} {count}
                          </Button>
                        </Tooltip>
                      );
                    })}
                    
                    {/* Quick reaction buttons for comments without reactions yet */}
                    {Object.keys(comment.reactions || {}).length === 0 && (
                      <>
                        {commonReactions.slice(0, 3).map((emoji) => (
                          <Button
                            key={`quick-${emoji}`}
                            size="small"
                            variant="text"
                            onClick={() => handleReactionClick(comment.id, emoji)}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '1rem',
                              color: '#666',
                              opacity: 0.7,
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: 'rgba(217, 141, 42, 0.1)',
                              }
                            }}
                          >
                            {emoji}
                          </Button>
                        ))}
                      </>
                    )}
                      {/* Emoji Picker Button for Reactions */}
                    <IconButton
                      onClick={(event) => handleReactionEmojiPickerOpen(event, comment.id)}
                      size="small"
                      sx={{
                        color: '#A9A9A9',
                        border: '1px dashed #666',
                        borderRadius: '16px',
                        ml: 0.5,
                        '&:hover': {
                          color: '#d98d2a',
                          borderColor: '#d98d2a',
                          backgroundColor: 'rgba(217, 141, 42, 0.1)',
                        }
                      }}
                      aria-label="add emoji reaction"
                      title="Add emoji reaction"
                    >
                      <EmojiEmotionsIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
          );
        })()}

      {/* Comment Form */}
      {currentUser && firestoreUser ? (
        <Paper sx={{ p: 3, backgroundColor: '#444', border: '1px solid #d98d2a' }}>
          <Typography variant="h6" sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif', mb: 2 }}>
            Add a Comment
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>}
          
          <form onSubmit={handleSubmitComment}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isInCharacter}
                    onChange={(e) => {
                      setIsInCharacter(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedCharacterId('');
                      }
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#66bb6a' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' }
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#F5F5F5', fontFamily: '"Georgia", serif' }}>
                    Post as character (In Character)
                  </Typography>
                }
              />
            </Box>

            {isInCharacter && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: '#d98d2a' }}>Select Character</InputLabel>
                <Select
                  value={selectedCharacterId}
                  onChange={(e) => setSelectedCharacterId(e.target.value)}
                  label="Select Character"
                  sx={{ 
                    color: '#F5F5F5', 
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                    '& .MuiSvgIcon-root': { color: '#d98d2a' }
                  }}
                >
                  {activeCharacters.map((character) => (
                    <MenuItem key={character.id} value={character.id}>
                      {character.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}            <Box sx={{ position: 'relative' }}>
              <TextField
                ref={textFieldRef}
                label={isInCharacter ? "Comment (In Character)" : "Comment"}
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                placeholder={isInCharacter 
                  ? "Share your character's thoughts about this episode... 😊" 
                  : "Share your thoughts about this episode... 😊"
                }
                InputLabelProps={{ style: { color: '#d98d2a' } }}
                inputProps={{ 
                  style: { 
                    color: '#F5F5F5', 
                    fontFamily: '"Georgia", serif',
                    fontStyle: isInCharacter ? 'italic' : 'normal'
                  }
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': { 
                    '& fieldset': { borderColor: '#d98d2a' },
                    '&:hover fieldset': { borderColor: '#c87d26' },
                    '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
                  }
                }}
              />
              
              {/* Emoji Picker Button */}
              <IconButton
                onClick={handleEmojiButtonClick}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 8,
                  color: '#d98d2a',
                  '&:hover': {
                    backgroundColor: 'rgba(217, 141, 42, 0.1)',
                  }
                }}
                aria-label="add emoji"
              >
                <EmojiEmotionsIcon />
              </IconButton>
              
              {/* Emoji Picker Popover */}
              <Popover
                open={Boolean(emojiPickerAnchor)}
                anchorEl={emojiPickerAnchor}
                onClose={handleEmojiPickerClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiPopover-paper': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                  }
                }}
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme="dark"
                  emojiStyle="native"
                  width={300}
                  height={400}
                />
              </Popover>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !commentContent.trim() || (isInCharacter && !selectedCharacterId)}
              sx={{
                backgroundColor: isInCharacter ? '#4caf50' : '#d98d2a',
                color: '#1c1c1c',
                fontFamily: '"Georgia", serif',
                '&:hover': { 
                  backgroundColor: isInCharacter ? '#388e3c' : '#c87d26' 
                },
                '&:disabled': { backgroundColor: 'grey' }
              }}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: '#1c1c1c' }} />
              ) : isInCharacter ? (
                'Post as Character'
              ) : (
                'Post Comment'
              )}
            </Button>
          </form>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#444', border: '1px solid #d98d2a' }}>
          <Typography sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif' }}>
            Please log in to post comments.
          </Typography>        </Paper>
      )}
      
      {/* Reaction Emoji Picker Popover */}
      <Popover
        open={Boolean(reactionPickerAnchor)}
        anchorEl={reactionPickerAnchor}
        onClose={handleReactionEmojiPickerClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <EmojiPicker
          onEmojiClick={handleReactionEmojiClick}
          theme="dark"
          emojiStyle="native"
          width={300}
          height={400}
          searchPlaceholder="Search for reaction emoji..."
        />
      </Popover>
    </Box>
  );
};

export default EpisodeComments;
